import { useMutation } from '@apollo/client/react';
import { ChevronDown } from 'lucide-react';
import { graphql } from '@/gql';
import { refreshAccessToken } from '@/api/auth';
import { useSession } from './useSession';

const SwitchScopeDoc = graphql(/* GraphQL */ `
  mutation SwitchScope($id: UUID!) {
    switchScope(id: $id) {
      id
      name
    }
  }
`);

/**
 * Compact dropdown that lives in the topbar. It lists the scopes the user is
 * allowed to access and calls `switchScope` on change; the resolver updates
 * the session, so we simply reload to make every subsequent GraphQL call use
 * the new scope.
 */
export function ScopeSwitcher() {
  const { me } = useSession();
  const [switchScope, { loading }] = useMutation(SwitchScopeDoc);

  if (!me || me.allowedScopes.length <= 1) {
    return null;
  }

  const currentId = me.currentScope?.id ?? me.defaultScope?.id ?? me.allowedScopes[0]?.id;

  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">Active scope</span>
      <select
        className="h-9 appearance-none rounded-md border border-gray-200 bg-white pl-3 pr-8 text-sm text-gray-700 transition hover:border-gray-300 focus:border-brand focus:outline-none disabled:opacity-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-700"
        value={currentId ?? ''}
        disabled={loading}
        onChange={async (event) => {
          const nextId = event.target.value;
          if (!nextId || nextId === currentId) return;
          await switchScope({ variables: { id: nextId } });
          await refreshAccessToken();
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }}
      >
        {me.allowedScopes.map((scope) => (
          <option key={scope.id} value={scope.id}>
            {scope.name}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2 h-4 w-4 text-gray-400"
        aria-hidden="true"
      />
    </label>
  );
}
